import { themeRawValues } from '@/utils/styleUtils';
import {
  Cell,
  Legend,
  LegendProps,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { GenderDataByPlatform } from './GenderDataByPlatform';
import { GenderPlaysResponse, SelectedPlatform } from '@/types/api/gender';
import { ChartTooltipWrapper } from '@/components/charts/ChartTooltip';
import { PercentageByPlatformTooltipContent } from '../PercentageByPlatformTooltipContent';
import {
  mixpanelTrack,
  RevenueDashboardWidget,
  uppercaseFirstLetter,
} from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { formatPercentage } from '@/utils/numberFormattingUtils';

const chartColors = themeRawValues.colors.chartColors;

const colors = [chartColors.color3, chartColors.color5, chartColors.color6];

export const GenderChart = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: GenderPlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data>;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const handleTooltipHover = (event: { name?: string; percent?: number }) => {
    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.chartTooltipHover, {
      widget: RevenueDashboardWidget.GenderDemographics,
      page: 'audience',
      platform: selectedPlatform,
      demographics: 'gender',
      segment: event?.name || '',
      percent: event.percent ? Math.round(event.percent * 100).toString() + '%' : '',
    });
  };

  const filteredData =
    data.plays.find((play) => play.platform === selectedPlatform)?.gender_distribution ?? [];

  const CustomLegend = (props: LegendProps) => {
    const { payload } = props;

    return (
      <ul className="flex flex-row flex-wrap gap-6 items-center justify-center">
        {payload?.map((entry: Payload, index: number) => (
          <li key={`legend-item-${index}`} className="inline-flex items-center gap-2">
            <figure style={{ backgroundColor: entry.color }} className="w-3 h-3 rounded-full" />
            <span className="text-textDarkGrey text-md">
              {uppercaseFirstLetter(entry.value?.toLowerCase())}
            </span>
            {entry.payload && (
              <span className="text-textMedGrey font-light text-sm">
                {formatPercentage(entry.payload?.value)}%
              </span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <ResponsiveContainer height={400} width={'100%'}>
        <PieChart
          style={{ width: '100%', aspectRatio: 1 }}
          margin={{ top: 0, right: 32, bottom: 32, left: 32 }}
        >
          <Tooltip
            cursor={false}
            isAnimationActive={true}
            offset={32}
            allowEscapeViewBox={{ y: true }}
            animationDuration={1000}
            content={({ active, payload }: TooltipProps<number, string>) => (
              <ChartTooltipWrapper className="max-w-64 border-[#F0F0F9] bg-[#FAFAFA] text-textDarkGrey">
                {active && payload?.[0]?.payload && (
                  <PercentageByPlatformTooltipContent
                    label={`${payload[0].payload.label}`}
                    value={Math.round(payload[0].payload.percentage || 0)}
                    selectedPlatform={selectedPlatform}
                  />
                )}
              </ChartTooltipWrapper>
            )}
          />
          <Pie
            animationBegin={100}
            animationDuration={500}
            data={filteredData}
            dataKey="percentage"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius="50%"
            outerRadius="80%"
            onMouseEnter={handleTooltipHover}
          >
            {filteredData.map((_, index) => (
              <Cell key={`cell-${index}`} stroke={colors[index]} fill={colors[index]} />
            ))}
          </Pie>
          <Legend
            verticalAlign="bottom"
            height={36}
            layout="horizontal"
            textAnchor="middle"
            content={<CustomLegend />}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="my-6 flex flex-row justify-center">
        <GenderDataByPlatform
          data={data}
          selectedPlatform={selectedPlatform}
          handlePlatformChange={handlePlatformChange}
        />
      </div>
    </>
  );
};
